import React, { useMemo, useState } from 'react';
import TaskQueueMatrixChart from './TaskQueueMatrixChart';
import WorkflowFlowchart from './WorkflowFlowchart';

function extractSkillFromTargetWorkers(expr) {
  const match = expr?.match(/"([^"]+)"/);
  return match ? match[1] : null;
}

export default function QueueWorkersContainer({
  queueData,
  selectedQueueSid,
  onSelectQueue,
  workers,
  loading,
  error,
  queuesWithoutWorkers = [],
  taskQueues = [],
  workflows = [],
  customers = []
}) {
  const [selectedTab, setSelectedTab] = useState('agents');
  // const [expandedCustomerKey, setExpandedCustomerKey] = useState(null); // unused
  const [modalData, setModalData] = useState(null);
  const [showWithoutName, setShowWithoutName] = useState(true);
  const [showWithoutServiceGroup, setShowWithoutServiceGroup] = useState(true);
  const [page, setPage] = useState(1);
  const [searchAll, setSearchAll] = useState('');

  const ITEMS_PER_PAGE = 12;
  const AGENTS_PER_PAGE = 10;
  const [agentsPage, setAgentsPage] = useState(1);


  const selectedQueue = queueData.find(q => q.queueSid === selectedQueueSid);
  const queueName = selectedQueue?.queueName || '';

  const targetQueue = selectedQueueSid
    ? taskQueues.find(q => q.sid === selectedQueueSid)
    : null;

  const skillName = selectedQueueSid
    ? extractSkillFromTargetWorkers(targetQueue?.targetWorkers || '')
    : '';

  // Combine enabled and disabled agents for the skill, deduplicated by sid
  const filteredWorkers = useMemo(() => {
    if (!skillName) return [];
    const enabled = workers.filter(worker => {
      try {
        const data = typeof worker.attributes === 'string' ? JSON.parse(worker.attributes) : worker.attributes;
        return data?.routing?.skills?.includes(skillName);
      } catch {
        return false;
      }
    });
    const disabled = workers.filter(worker => {
      try {
        const data = typeof worker.attributes === 'string' ? JSON.parse(worker.attributes) : worker.attributes;
        return data?.disabled_skills?.skills?.includes(skillName);
      } catch {
        return false;
      }
    });
    // Deduplicate by sid
    const all = [...enabled, ...disabled];
    const seen = new Set();
    return all.filter(w => {
      if (seen.has(w.sid)) return false;
      seen.add(w.sid);
      return true;
    });
  }, [workers, skillName]);

  const relatedWorkflows = useMemo(() => {
    return workflows.filter(wf => {
      try {
        const config = JSON.parse(wf.configuration);
        const filters = config.task_routing?.filters || [];
        return filters.some(filter =>
          filter.targets?.some(t => t.queue === selectedQueueSid)
        );
      } catch {
        return false;
      }
    });
  }, [workflows, selectedQueueSid]);

  const matchingWorkflowName = relatedWorkflows[0]?.friendlyName;

  const matchingCustomers = useMemo(() => {
    if (!matchingWorkflowName) return [];
    return customers.filter(cust => {
      const sg = cust.data?.serviceGroup || '';
      return sg.toLowerCase().trim() === matchingWorkflowName.toLowerCase().trim();
    });
  }, [customers, matchingWorkflowName]);

  const allFilteredCustomers = useMemo(() => {
    const lower = searchAll.toLowerCase();
    return customers.filter(c => {
      const noName = !c.data?.name?.trim();
      const noServiceGroup = !c.data?.serviceGroup?.trim();

      const passesNameFilter = showWithoutName ? noName : !noName;
      const passesServiceGroupFilter = showWithoutServiceGroup ? noServiceGroup : !noServiceGroup;

      const passesSearch =
        c.data?.name?.toLowerCase().includes(lower) ||
        c.key.toLowerCase().includes(lower) ||
        c.data?.serviceGroup?.toLowerCase().includes(lower);

      return passesNameFilter && passesServiceGroupFilter && passesSearch;
    });
  }, [customers, searchAll, showWithoutName, showWithoutServiceGroup]);

  const paginatedCustomers = useMemo(() => {
    const start = (page - 1) * ITEMS_PER_PAGE;
    return allFilteredCustomers.slice(start, start + ITEMS_PER_PAGE);
  }, [allFilteredCustomers, page]);

  const totalPages = Math.ceil(allFilteredCustomers.length / ITEMS_PER_PAGE);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', width: '100%', gap: 30, fontFamily: 'Wix Madefor Text, sans-serif' }}>
      <div style={{ flex: 1 }}>
        <TaskQueueMatrixChart
          data={queueData}
          onSelectQueue={onSelectQueue}
          queuesWithoutWorkers={queuesWithoutWorkers}
        />
      </div>

      <div style={{ display: 'flex', flexDirection: 'row', gap: 30, minHeight: '400px' }}>
        {/* Right panel with tabs */}
        <div
          style={{
            flex: 1,
            backgroundColor: '#fff',
            overflowY: 'auto',
            borderRadius: 14,
            boxShadow: '0 2px 12px rgba(44,62,80,0.07)',
            padding: '18px 0 0 0',
          }}
        >
          {/* Modern Tab Switcher */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginBottom: 8 }}>
            <button
                onClick={() => setSelectedTab('agents')}
                style={{
                  padding: '4px 14px',
                  background: selectedTab === 'agents' ? '#2d4a7a' : '#fff',
                  color: selectedTab === 'agents' ? '#fff' : '#2d4a7a',
                  border: selectedTab === 'agents' ? '1px solid #2d4a7a' : '1px solid #e3eaf3',
                  borderRadius: 16,
                  fontWeight: 500,
                  fontSize: 13,
                  boxShadow: selectedTab === 'agents' ? '0 1px 4px rgba(44,62,80,0.06)' : 'none',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
              >👤 Agents</button>
            <button
                onClick={() => setSelectedTab('customers')}
                style={{
                  padding: '4px 14px',
                  background: selectedTab === 'customers' ? '#2d4a7a' : '#fff',
                  color: selectedTab === 'customers' ? '#fff' : '#2d4a7a',
                  border: selectedTab === 'customers' ? '1px solid #2d4a7a' : '1px solid #e3eaf3',
                  borderRadius: 16,
                  fontWeight: 500,
                  fontSize: 13,
                  boxShadow: selectedTab === 'customers' ? '0 1px 4px rgba(44,62,80,0.06)' : 'none',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
              >🧑‍💼 Klanten</button>
          </div>

          {/* Tab Content */}
          <div style={{ padding: '10px 24px 24px 24px' }}>
            {selectedTab === 'agents' && (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, borderBottom: '1px solid #e3eaf3', paddingBottom: 6 }}>
                  <h3 style={{ fontSize: 12, fontWeight: 500, color: '#2d4a7a', margin: 0, display: 'flex', alignItems: 'center', gap: 4 }}>👤 Agenten met Skill <span style={{ fontWeight: 400, color: '#3a4a5a', fontSize: 11 }}>— <em>{skillName}</em></span></h3>
                  <h3 style={{ fontSize: 12, fontWeight: 500, color: '#2d4a7a', margin: 0, display: 'flex', alignItems: 'center', gap: 4 }}>🛠 Skillgroep <span style={{ fontWeight: 400, color: '#3a4a5a', fontSize: 11 }}>— <em>{queueName}</em></span></h3>
                </div>
                {!selectedQueueSid && <div style={{ textAlign: 'center', color: '#b83d3d', fontWeight: 500, margin: '24px 0' }}>Selecteer een skillgroep om agenten te zien.</div>}
                {loading && <div style={{ textAlign: 'center', color: '#2d4a7a', margin: '24px 0' }}>Laden agenten...</div>}
                {error && <div style={{ textAlign: 'center', color: 'red', margin: '24px 0' }}>{error}</div>}
                {!loading && !error && selectedQueueSid && filteredWorkers.length === 0 && (
                  <div style={{ textAlign: 'center', color: '#b83d3d', fontWeight: 500, margin: '24px 0' }}>Geen agenten gevonden voor deze skillgroep.</div>
                )}
                <div style={{ width: '100%' }}>
                  <div style={{ display: 'flex', flexDirection: 'row', gap: 10, width: '100%' }}>
                    {(() => {
                      const pageWorkers = filteredWorkers.slice((agentsPage - 1) * AGENTS_PER_PAGE, agentsPage * AGENTS_PER_PAGE);
                      const col1 = pageWorkers.slice(0, 5);
                      const col2 = pageWorkers.slice(5, 10);
                      return (
                        <>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '50%' }}>
                            {col1.map(worker => (
                              <div key={worker.sid} style={{ background: '#f7fafd', borderRadius: 8, padding: '7px 12px', boxShadow: '0 1px 6px rgba(44,62,80,0.04)', display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                                <span style={{ width: 24, height: 24, borderRadius: '50%', background: '#2d4a7a', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13 }}>{worker.friendlyName.charAt(0).toUpperCase()}</span>
                                <span style={{ fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{worker.friendlyName.split('@')[0].replace(/\./g, ' ')}</span>
                                <span style={{ fontSize: 12, color: '#b83d3d', marginLeft: 'auto' }}>
                                  {(() => {
                                    try {
                                      const data = typeof worker.attributes === 'string' ? JSON.parse(worker.attributes) : worker.attributes;
                                      const hasSkill = data?.routing?.skills?.includes(skillName);
                                      const isDisabled = data?.disabled_skills?.skills?.includes(skillName);
                                      if (isDisabled) {
                                        const disabledLevel = data?.disabled_skills?.levels?.[skillName];
                                        return disabledLevel ? `disabled (level ${disabledLevel})` : 'disabled';
                                      }
                                      if (hasSkill) {
                                        const level = data?.routing?.levels?.[skillName];
                                        return level ? `skill level ${level}` : 'skill level unknown';
                                      }
                                      return 'disabled';
                                    } catch {
                                      return 'disabled';
                                    }
                                  })()}
                                </span>
                              </div>
                            ))}
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '50%' }}>
                            {col2.map(worker => (
                              <div key={worker.sid} style={{ background: '#f7fafd', borderRadius: 8, padding: '7px 12px', boxShadow: '0 1px 6px rgba(44,62,80,0.04)', display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                                <span style={{ width: 24, height: 24, borderRadius: '50%', background: '#2d4a7a', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13 }}>{worker.friendlyName.charAt(0).toUpperCase()}</span>
                                <span style={{ fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{worker.friendlyName.split('@')[0].replace(/\./g, ' ')}</span>
                                <span style={{ fontSize: 12, color: '#b83d3d', marginLeft: 'auto' }}>
                                  {(() => {
                                    try {
                                      const data = typeof worker.attributes === 'string' ? JSON.parse(worker.attributes) : worker.attributes;
                                      const hasSkill = data?.routing?.skills?.includes(skillName);
                                      const isDisabled = data?.disabled_skills?.skills?.includes(skillName);
                                      if (isDisabled) {
                                        const disabledLevel = data?.disabled_skills?.levels?.[skillName];
                                        return disabledLevel ? `disabled (level ${disabledLevel})` : 'disabled';
                                      }
                                      if (hasSkill) {
                                        const level = data?.routing?.levels?.[skillName];
                                        return level ? `skill level ${level}` : 'skill level unknown';
                                      }
                                      return 'disabled';
                                    } catch {
                                      return 'disabled';
                                    }
                                  })()}
                                </span>
                              </div>
                            ))}
                          </div>
                        </>
                      );
                    })()}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 10, marginTop: 16, width: '100%' }}>
                    <button
                      onClick={() => setAgentsPage(p => Math.max(p - 1, 1))}
                      disabled={agentsPage === 1}
                      style={{ padding: '4px 14px', borderRadius: 16, border: '1px solid #e3eaf3', background: '#fff', color: '#2d4a7a', cursor: agentsPage === 1 ? 'not-allowed' : 'pointer' }}
                    >Vorige</button>
                    <span style={{ fontSize: 13 }}>Pagina {agentsPage} van {Math.max(1, Math.ceil(filteredWorkers.length / AGENTS_PER_PAGE))}</span>
                    <button
                      onClick={() => setAgentsPage(p => Math.min(p + 1, Math.max(1, Math.ceil(filteredWorkers.length / AGENTS_PER_PAGE))))}
                      disabled={agentsPage === Math.max(1, Math.ceil(filteredWorkers.length / AGENTS_PER_PAGE))}
                      style={{ padding: '4px 14px', borderRadius: 16, border: '1px solid #e3eaf3', background: '#fff', color: '#2d4a7a', cursor: agentsPage === Math.max(1, Math.ceil(filteredWorkers.length / AGENTS_PER_PAGE)) ? 'not-allowed' : 'pointer' }}
                    >Volgende</button>
                  </div>
                </div>
              </>
            )}

            {selectedTab === 'customers' && (
              <>
                <h3 style={{ fontSize: 12, fontWeight: 500, color: '#2d4a7a', marginBottom: 12, borderBottom: '1px solid #e3eaf3', paddingBottom: 6, display: 'flex', alignItems: 'center', gap: 4 }}>🧑‍💼 Klanten voor servicegroep <span style={{ fontWeight: 400, color: '#3a4a5a', fontSize: 11 }}>— <em>{matchingWorkflowName}</em></span></h3>
                {matchingCustomers.length === 0 ? (
                  <div style={{ textAlign: 'center', color: '#b83d3d', fontWeight: 500, margin: '24px 0' }}>Geen klanten gevonden voor deze servicegroep.</div>
                ) : (
                  <div style={{ width: '100%' }}>
                    <div style={{ display: 'flex', flexDirection: 'row', gap: 10, width: '100%' }}>
                      {(() => {
                        const CUSTOMERS_PER_PAGE = 10;
                        // const [custPage, setCustPage] = [agentsPage, setAgentsPage]; // unused, use agentsPage/setAgentsPage directly
                        const pageCustomers = matchingCustomers.slice((agentsPage - 1) * CUSTOMERS_PER_PAGE, agentsPage * CUSTOMERS_PER_PAGE);
                        const col1 = pageCustomers.slice(0, 5);
                        const col2 = pageCustomers.slice(5, 10);
                        return (
                          <>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '50%' }}>
                              {col1.map(cust => (
                                <div key={cust.key} style={{ background: '#f7fafd', borderRadius: 8, padding: '7px 12px', boxShadow: '0 1px 6px rgba(44,62,80,0.04)', display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                                  <span style={{ width: 24, height: 24, borderRadius: '50%', background: '#b83d3d', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13 }}>{cust.data.name ? cust.data.name.charAt(0).toUpperCase() : '?'}</span>
                                  <span style={{ fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{cust.data.name || 'Naam onbekend'}</span>
                                  <span style={{ fontSize: 12, color: '#2d4a7a', marginLeft: 'auto' }}>{cust.data.forwardingNumber || 'Geen nummer'}</span>
                                </div>
                              ))}
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '50%' }}>
                              {col2.map(cust => (
                                <div key={cust.key} style={{ background: '#f7fafd', borderRadius: 8, padding: '7px 12px', boxShadow: '0 1px 6px rgba(44,62,80,0.04)', display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                                  <span style={{ width: 24, height: 24, borderRadius: '50%', background: '#b83d3d', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13 }}>{cust.data.name ? cust.data.name.charAt(0).toUpperCase() : '?'}</span>
                                  <span style={{ fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{cust.data.name || 'Naam onbekend'}</span>
                                  <span style={{ fontSize: 12, color: '#2d4a7a', marginLeft: 'auto' }}>{cust.data.forwardingNumber || 'Geen nummer'}</span>
                                </div>
                              ))}
                            </div>
                          </>
                        );
                      })()}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 10, marginTop: 16, width: '100%' }}>
                      <button
                        onClick={() => setAgentsPage(p => Math.max(p - 1, 1))}
                        disabled={agentsPage === 1}
                        style={{ padding: '4px 14px', borderRadius: 16, border: '1px solid #e3eaf3', background: '#fff', color: '#2d4a7a', cursor: agentsPage === 1 ? 'not-allowed' : 'pointer' }}
                      >Vorige</button>
                      <span style={{ fontSize: 13 }}>Pagina {agentsPage} van {Math.max(1, Math.ceil(matchingCustomers.length / 10))}</span>
                      <button
                        onClick={() => setAgentsPage(p => Math.min(p + 1, Math.max(1, Math.ceil(matchingCustomers.length / 10))))}
                        disabled={agentsPage === Math.max(1, Math.ceil(matchingCustomers.length / 10))}
                        style={{ padding: '4px 14px', borderRadius: 16, border: '1px solid #e3eaf3', background: '#fff', color: '#2d4a7a', cursor: agentsPage === Math.max(1, Math.ceil(matchingCustomers.length / 10)) ? 'not-allowed' : 'pointer' }}
                      >Volgende</button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Flowchart panel */}
        <div style={{ flex: 1, backgroundColor: '#fff' }}>
          <WorkflowFlowchart
            workflows={relatedWorkflows}
            workflowData={workflows}
            queueData={queueData}
            selectedQueueSid={selectedQueueSid}
          />
        </div>
      </div>

      {/* All Customers Section with filters */}
      <div style={{ background: '#f7fafd', borderRadius: 14, boxShadow: '0 2px 12px rgba(44,62,80,0.07)', padding: 24, border: '1px solid #e3eaf3', marginTop: 20, minHeight: 400, display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
        <h3 style={{ fontSize: 16, fontWeight: 600, color: '#2d4a7a', marginBottom: 18, display: 'flex', alignItems: 'center', gap: 8 }}>🧾 Alle klanten</h3>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, marginBottom: 18 }}>
          <input
            type="text"
            placeholder="Zoek klant op naam of ID..."
            value={searchAll}
            onChange={e => setSearchAll(e.target.value)}
            style={{ padding: 8, borderRadius: 8, border: '1px solid #e3eaf3', flex: 1, minWidth: '200px', fontSize: 14 }}
          />
          <label style={{ fontSize: 13, color: '#2d4a7a', display: 'flex', alignItems: 'center', gap: 6 }}>
            <input
              type="checkbox"
              checked={showWithoutName}
              onChange={() => setShowWithoutName(v => !v)}
              style={{ marginRight: 6 }}
            />
            Zonder naam
          </label>
          <label style={{ fontSize: 13, color: '#2d4a7a', display: 'flex', alignItems: 'center', gap: 6 }}>
            <input
              type="checkbox"
              checked={showWithoutServiceGroup}
              onChange={() => setShowWithoutServiceGroup(v => !v)}
              style={{ marginRight: 6 }}
            />
            Zonder servicegroep
          </label>
        </div>

        {paginatedCustomers.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#b83d3d', fontWeight: 500, margin: '24px 0' }}>Geen klanten gevonden met de huidige filters.</div>
        ) : (
          <>
            <ul style={{
              listStyleType: 'none',
              paddingLeft: 0,
              marginBottom: 18,
              width: '100%',
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gridAutoRows: '1fr',
              gap: '20px'
            }}>
              {paginatedCustomers.map(cust => (
                <li key={cust.key} style={{ background: '#fff', borderRadius: 10, boxShadow: '0 1px 6px rgba(44,62,80,0.04)', border: '1px solid #e3eaf3', padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: 6, position: 'relative' }}>
                  <button
                    onClick={() => setModalData(cust.data)}
                    style={{
                      position: 'absolute',
                      top: 14,
                      right: 18,
                      fontSize: 12,
                      padding: '4px 10px',
                      borderRadius: 8,
                      border: '1px solid #e3eaf3',
                      background: '#f7fafd',
                      color: '#2d4a7a',
                      cursor: 'pointer',
                      zIndex: 2
                    }}
                  >Toon JSON</button>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ width: 32, height: 32, borderRadius: '50%', background: '#2d4a7a', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600, fontSize: 13, letterSpacing: 1, padding: '2px' }}>{cust.data.id ?? '?'}</span>
                    <span style={{ fontSize: 15, fontWeight: 600, color: '#2d4a7a' }}>{cust.data.name || 'Naam onbekend'}</span>
                  </div>
                  <span style={{ fontSize: 13, color: '#2d4a7a' }}>Nummer: <strong>{cust.data.forwardingNumber || 'unknown'}</strong></span>
                  <span style={{ fontSize: 13, color: '#2d4a7a' }}>Servicegroep: <strong>{cust.data.serviceGroup || 'onbekend'}</strong></span>
                </li>
              ))}
      {/* Modal for JSON display */}
      {modalData && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(44,62,80,0.18)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{
            background: '#fff',
            borderRadius: 14,
            boxShadow: '0 2px 12px rgba(44,62,80,0.18)',
            padding: 32,
            minWidth: 340,
            maxWidth: 600,
            maxHeight: '80vh',
            overflowY: 'auto',
            position: 'relative'
          }}>
            <button
              onClick={() => setModalData(null)}
              style={{
                position: 'absolute',
                top: 16,
                right: 16,
                fontSize: 13,
                padding: '4px 12px',
                borderRadius: 8,
                border: '1px solid #e3eaf3',
                background: '#f7fafd',
                color: '#2d4a7a',
                cursor: 'pointer',
                fontWeight: 500
              }}
            >Sluiten</button>
            <h3 style={{ fontSize: 16, fontWeight: 600, color: '#2d4a7a', marginBottom: 18 }}>Klant JSON</h3>
            <pre style={{ fontSize: 13, backgroundColor: '#f9f9f9', padding: 16, borderRadius: 8, border: '1px solid #e3eaf3', maxWidth: '100%', overflowX: 'auto' }}>
              {JSON.stringify(modalData, null, 2)}
            </pre>
          </div>
        </div>
      )}
            </ul>

            {/* Pagination Controls */}
            <div style={{ display: 'flex', justifyContent: 'center', gap: 10, marginTop: 10 }}>
              <button
                onClick={() => setPage(p => Math.max(p - 1, 1))}
                disabled={page === 1}
                style={{ padding: '4px 14px', borderRadius: 16, border: '1px solid #e3eaf3', background: '#fff', color: '#2d4a7a', cursor: page === 1 ? 'not-allowed' : 'pointer' }}
              >
                Vorige
              </button>
              <span style={{ fontSize: 13 }}>Pagina {page} van {totalPages}</span>
              <button
                onClick={() => setPage(p => Math.min(p + 1, totalPages))}
                disabled={page === totalPages}
                style={{ padding: '4px 14px', borderRadius: 16, border: '1px solid #e3eaf3', background: '#fff', color: '#2d4a7a', cursor: page === totalPages ? 'not-allowed' : 'pointer' }}
              >
                Volgende
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
